import { useEffect, useMemo, useState } from "react"
import { countries, nationalities } from "../../utils/countryData"
import InfoPopover from "../ui/InfoPopover"

type ServiceAddress = {
  street: string
  line2: string
  poBox: string
  postalCode: string
  city: string
  region: string
  country: string
}

const EMPTY_SERVICE_ADDRESS: ServiceAddress = {
  street: "",
  line2: "",
  poBox: "",
  postalCode: "",
  city: "",
  region: "",
  country: ""
}

// EP1001 applicant form (SCRUM-55), modelled field-for-field on the real
// EPO "Online-Einreichung 2.0" tool. Deliberately separate from the
// shared Trademark/Design applicant form, which is oriented towards
// EUIPO concepts (company size, activity sector, …) that do not apply
// to an EP filing.

type PatentApplicantFormProps = {
  type: "natural" | "legal"
  initialValues?: Record<string, any>
  onSave: (data: any) => void
  onCancel: () => void
  disabled?: boolean
}

type FieldErrors = Record<string, boolean>

const inputClass = (hasError: boolean, disabled: boolean) =>
  `w-full rounded-md border px-3 py-2 text-sm transition focus:border-dantzBlue focus:ring-1 focus:ring-dantzBlue ${
    hasError ? "border-red-500 bg-white" : "border-gray-300 bg-white"
  } ${disabled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}`

export default function PatentApplicantForm({
  type,
  initialValues,
  onSave,
  onCancel,
  disabled = false
}: PatentApplicantFormProps) {
  const isLegal = type === "legal"

  const [commonRepresentative, setCommonRepresentative] = useState(false)
  const [isAlsoInventor, setIsAlsoInventor] = useState(false)
  const [lastName, setLastName] = useState("")
  const [firstName, setFirstName] = useState("")
  const [nationality, setNationality] = useState("")
  const [countryOfResidence, setCountryOfResidence] = useState("")
  const [organisationName, setOrganisationName] = useState("")
  const [legalName, setLegalName] = useState("")
  const [department, setDepartment] = useState("")
  const [street, setStreet] = useState("")
  const [line2, setLine2] = useState("")
  const [poBox, setPoBox] = useState("")
  const [postalCode, setPostalCode] = useState("")
  const [city, setCity] = useState("")
  const [region, setRegion] = useState("")
  const [country, setCountry] = useState("")
  const [phone, setPhone] = useState("")
  const [email, setEmail] = useState("")
  const [useServiceAddress, setUseServiceAddress] = useState(false)
  const [serviceAddress, setServiceAddress] = useState<ServiceAddress>(
    EMPTY_SERVICE_ADDRESS
  )
  const [useEmployeeRepresentative, setUseEmployeeRepresentative] = useState(false)
  const [employeeFirstName, setEmployeeFirstName] = useState("")
  const [employeeLastName, setEmployeeLastName] = useState("")
  const [errors, setErrors] = useState<FieldErrors>({})

  useEffect(() => {
    const v = initialValues || {}
    const address = v.address || {}
    const contact = v.contact || {}

    setCommonRepresentative(Boolean(v.commonRepresentative))
    setIsAlsoInventor(Boolean(v.isAlsoInventor))
    setLastName(v.lastName || "")
    setFirstName(v.firstName || "")
    setNationality(v.nationality || "")
    setCountryOfResidence(v.countryOfResidence || "")
    setOrganisationName(v.organisationName || "")
    setLegalName(v.legalName || "")
    setDepartment(v.department || "")
    setStreet(address.street || "")
    setLine2(address.line2 || "")
    setPoBox(address.poBox || "")
    setPostalCode(address.postalCode || "")
    setCity(address.city || "")
    setRegion(address.region || "")
    setCountry(address.country || "")
    setPhone(contact.phone || "")
    setEmail(contact.email || "")

    const svc = v.serviceAddress
    const hasServiceAddress = Boolean(
      svc && Object.values(svc).some((field) => field)
    )
    setUseServiceAddress(hasServiceAddress)
    setServiceAddress(
      hasServiceAddress
        ? { ...EMPTY_SERVICE_ADDRESS, ...svc }
        : EMPTY_SERVICE_ADDRESS
    )

    const employee = v.employeeRepresentative
    const hasEmployee = Boolean(employee && (employee.firstName || employee.lastName))
    setUseEmployeeRepresentative(hasEmployee)
    setEmployeeFirstName(employee?.firstName || "")
    setEmployeeLastName(employee?.lastName || "")

    setErrors({})
  }, [initialValues])

  const requiredFields = useMemo(() => {
    const base: Record<string, string> = {
      city,
      country
    }

    if (isLegal) {
      base.legalName = legalName
      base.countryOfResidence = countryOfResidence
    } else {
      base.lastName = lastName
      base.firstName = firstName
      base.nationality = nationality
      base.countryOfResidence = countryOfResidence
    }

    return base
  }, [
    isLegal,
    city,
    country,
    legalName,
    countryOfResidence,
    lastName,
    firstName,
    nationality
  ])

  const clearError = (name: string) =>
    setErrors((prev) => ({ ...prev, [name]: false }))

  const handleSubmit = () => {
    if (disabled) return

    const nextErrors: FieldErrors = {}
    Object.entries(requiredFields).forEach(([name, value]) => {
      if (!value || !value.trim()) nextErrors[name] = true
    })

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return
    }

    onSave({
      type,
      commonRepresentative,
      isAlsoInventor: isLegal ? false : isAlsoInventor,
      lastName: isLegal ? undefined : lastName.trim(),
      firstName: isLegal ? undefined : firstName.trim(),
      nationality: isLegal ? undefined : nationality || undefined,
      organisationName: isLegal ? undefined : organisationName.trim() || undefined,
      legalName: isLegal ? legalName.trim() : undefined,
      department: department.trim() || undefined,
      countryOfResidence: countryOfResidence || undefined,
      address: {
        street: street.trim() || undefined,
        line2: line2.trim() || undefined,
        poBox: poBox.trim() || undefined,
        postalCode: postalCode.trim() || undefined,
        city: city.trim(),
        region: region.trim() || undefined,
        country
      },
      contact: {
        phone: phone.trim() || undefined,
        email: email.trim() || undefined
      },
      serviceAddress:
        useServiceAddress &&
        Object.values(serviceAddress).some((field) => field.trim())
          ? {
              street: serviceAddress.street.trim() || undefined,
              line2: serviceAddress.line2.trim() || undefined,
              poBox: serviceAddress.poBox.trim() || undefined,
              postalCode: serviceAddress.postalCode.trim() || undefined,
              city: serviceAddress.city.trim() || undefined,
              region: serviceAddress.region.trim() || undefined,
              country: serviceAddress.country.trim() || undefined
            }
          : null,
      employeeRepresentative:
        isLegal && useEmployeeRepresentative && employeeLastName.trim()
          ? {
              firstName: employeeFirstName.trim() || undefined,
              lastName: employeeLastName.trim()
            }
          : null
    })
  }

  const sectionTitle = "mt-6 mb-3 border-b border-gray-200 pb-1 text-sm font-semibold text-gray-800 first:mt-0"
  const labelClass = "text-sm font-medium text-gray-700"

  const setSvc = (key: keyof ServiceAddress, value: string) =>
    setServiceAddress((prev) => ({ ...prev, [key]: value }))

  const svcField = (key: keyof ServiceAddress, label: string) => (
    <div className="flex flex-col gap-1">
      <label className={labelClass}>{label}</label>
      <input
        type="text"
        value={serviceAddress[key]}
        disabled={disabled}
        onChange={(e) => setSvc(key, e.target.value)}
        className={inputClass(false, disabled)}
      />
    </div>
  )

  return (
    <div className="space-y-2">
      <h4 className={sectionTitle}>Details</h4>

      <div className="flex items-center gap-1.5">
        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={commonRepresentative}
            disabled={disabled}
            onChange={(e) => setCommonRepresentative(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-dantzBlue focus:ring-dantzBlue"
          />
          Common representative
        </label>
        <InfoPopover text="With several applicants, one can be designated as the common representative for all EPO communications. Without a designation, the first-named applicant is deemed the common representative (Rule 151(1) EPC)." />
      </div>

      {!isLegal && (
        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={isAlsoInventor}
            disabled={disabled}
            onChange={(e) => setIsAlsoInventor(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-dantzBlue focus:ring-dantzBlue"
          />
          Applicant is also inventor
        </label>
      )}

      {isLegal ? (
        <div className="flex flex-col gap-1 pt-2">
          <label className={labelClass}>Name of organisation *</label>
          <input
            type="text"
            value={legalName}
            disabled={disabled}
            onChange={(e) => {
              setLegalName(e.target.value)
              clearError("legalName")
            }}
            className={inputClass(Boolean(errors.legalName), disabled)}
          />
        </div>
      ) : (
        <div className="grid gap-4 pt-2 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <label className={labelClass}>Last name *</label>
            <input
              type="text"
              value={lastName}
              disabled={disabled}
              onChange={(e) => {
                setLastName(e.target.value)
                clearError("lastName")
              }}
              className={inputClass(Boolean(errors.lastName), disabled)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className={labelClass}>First name *</label>
            <input
              type="text"
              value={firstName}
              disabled={disabled}
              onChange={(e) => {
                setFirstName(e.target.value)
                clearError("firstName")
              }}
              className={inputClass(Boolean(errors.firstName), disabled)}
            />
          </div>
        </div>
      )}

      <div className="grid gap-4 pt-2 sm:grid-cols-2">
        {!isLegal && (
          <div className="flex flex-col gap-1">
            <label className={labelClass}>Nationality *</label>
            <select
              value={nationality}
              disabled={disabled}
              onChange={(e) => {
                setNationality(e.target.value)
                clearError("nationality")
              }}
              className={inputClass(Boolean(errors.nationality), disabled)}
            >
              <option value="">Select</option>
              {nationalities.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <label className={labelClass}>
            {isLegal
              ? "State of residence or principal place of business *"
              : "Country/territory of residence *"}
          </label>
          <select
            value={countryOfResidence}
            disabled={disabled}
            onChange={(e) => {
              setCountryOfResidence(e.target.value)
              clearError("countryOfResidence")
            }}
            className={inputClass(Boolean(errors.countryOfResidence), disabled)}
          >
            <option value="">Select</option>
            {countries.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid gap-4 pt-2 sm:grid-cols-2">
        {!isLegal && (
          <div className="flex flex-col gap-1">
            <label className={labelClass}>Name of organisation</label>
            <input
              type="text"
              value={organisationName}
              disabled={disabled}
              onChange={(e) => setOrganisationName(e.target.value)}
              className={inputClass(false, disabled)}
            />
          </div>
        )}
        <div className="flex flex-col gap-1">
          <label className={labelClass}>Department</label>
          <input
            type="text"
            value={department}
            disabled={disabled}
            onChange={(e) => setDepartment(e.target.value)}
            className={inputClass(false, disabled)}
          />
        </div>
      </div>

      <h4 className={sectionTitle}>Address</h4>

      <div className="flex flex-col gap-1">
        <label className={labelClass}>Address line 1</label>
        <input
          type="text"
          value={street}
          disabled={disabled}
          onChange={(e) => setStreet(e.target.value)}
          className={inputClass(false, disabled)}
        />
      </div>

      <div className="flex flex-col gap-1 pt-2">
        <label className={labelClass}>Address line 2</label>
        <input
          type="text"
          value={line2}
          disabled={disabled}
          onChange={(e) => setLine2(e.target.value)}
          className={inputClass(false, disabled)}
        />
      </div>

      <div className="grid gap-4 pt-2 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label className={labelClass}>P.O. box</label>
          <input
            type="text"
            value={poBox}
            disabled={disabled}
            onChange={(e) => setPoBox(e.target.value)}
            className={inputClass(false, disabled)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={labelClass}>Postal code</label>
          <input
            type="text"
            value={postalCode}
            disabled={disabled}
            onChange={(e) => setPostalCode(e.target.value)}
            className={inputClass(false, disabled)}
          />
        </div>
      </div>

      <div className="grid gap-4 pt-2 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label className={labelClass}>City *</label>
          <input
            type="text"
            value={city}
            disabled={disabled}
            onChange={(e) => {
              setCity(e.target.value)
              clearError("city")
            }}
            className={inputClass(Boolean(errors.city), disabled)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={labelClass}>Region / canton / state</label>
          <input
            type="text"
            value={region}
            disabled={disabled}
            onChange={(e) => setRegion(e.target.value)}
            className={inputClass(false, disabled)}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1 pt-2">
        <label className={labelClass}>Country/territory *</label>
        <select
          value={country}
          disabled={disabled}
          onChange={(e) => {
            setCountry(e.target.value)
            clearError("country")
          }}
          className={inputClass(Boolean(errors.country), disabled)}
        >
          <option value="">Select</option>
          {countries.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      <label className="mt-4 flex cursor-pointer items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={useServiceAddress}
          disabled={disabled}
          onChange={(e) => setUseServiceAddress(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-dantzBlue focus:ring-dantzBlue"
        />
        Use a different address for service
      </label>

      {useServiceAddress && (
        <div className="mt-3 space-y-2 rounded-lg border border-gray-200 bg-gray-50/60 p-4">
          {svcField("street", "Address line 1")}
          {svcField("line2", "Address line 2")}
          <div className="grid gap-4 sm:grid-cols-2">
            {svcField("poBox", "P.O. box")}
            {svcField("postalCode", "Postal code")}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {svcField("city", "City")}
            {svcField("region", "Region / canton / state")}
          </div>
          <div className="flex flex-col gap-1">
            <label className={labelClass}>Country/territory</label>
            <select
              value={serviceAddress.country}
              disabled={disabled}
              onChange={(e) => setSvc("country", e.target.value)}
              className={inputClass(false, disabled)}
            >
              <option value="">Select</option>
              {countries.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <h4 className={sectionTitle}>Phone and email</h4>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label className={labelClass}>Phone number</label>
          <input
            type="text"
            value={phone}
            disabled={disabled}
            onChange={(e) => setPhone(e.target.value)}
            className={inputClass(false, disabled)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={labelClass}>Email</label>
          <input
            type="text"
            value={email}
            disabled={disabled}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass(false, disabled)}
          />
        </div>
      </div>

      {isLegal && (
        <>
          <label className="mt-4 flex cursor-pointer items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={useEmployeeRepresentative}
              disabled={disabled}
              onChange={(e) => setUseEmployeeRepresentative(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-dantzBlue focus:ring-dantzBlue"
            />
            Represented by an employee (Art. 133(3) EPC)
          </label>

          {useEmployeeRepresentative && (
            <div className="mt-3 grid gap-4 rounded-lg border border-gray-200 bg-gray-50/60 p-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <label className={labelClass}>Employee last name</label>
                <input
                  type="text"
                  value={employeeLastName}
                  disabled={disabled}
                  onChange={(e) => setEmployeeLastName(e.target.value)}
                  className={inputClass(false, disabled)}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className={labelClass}>Employee first name</label>
                <input
                  type="text"
                  value={employeeFirstName}
                  disabled={disabled}
                  onChange={(e) => setEmployeeFirstName(e.target.value)}
                  className={inputClass(false, disabled)}
                />
              </div>
            </div>
          )}
        </>
      )}

      {Object.keys(errors).some((k) => errors[k]) && (
        <p className="pt-2 text-xs text-red-600">
          Please fill in all required fields.
        </p>
      )}

      <div className="flex flex-wrap gap-3 pt-5">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-50"
        >
          Cancel
        </button>
        {!disabled && (
          <button
            type="button"
            onClick={handleSubmit}
            className="rounded-lg bg-dantzBlue px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
          >
            Save
          </button>
        )}
      </div>
    </div>
  )
}
